import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/app/Modal";
import { personErrors } from "@/lib/personValidation";
import { btnOutline, btnPrimary, inputCls, inputErrCls, labelCls } from "@/components/app/ui";

const blank = { name: "", mobile: "", email: "", category: "" };
const NEW = "__new__";

/** Store owner adds a customer by hand, or edits one (pass `customer`): name, phone, email and category.
 *  Addresses are added separately (customer page, or the Address + button in the POS). */
export default function CustomerModal({ open, customer, onClose, onSaved }) {
  const [f, setF] = useState(blank);
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [adding, setAdding] = useState(false);        // "+ Add new category" row is open
  const [newCat, setNewCat] = useState("");
  const [catBusy, setCatBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ ...blank, name: customer?.name || "", mobile: customer?.mobile || "", email: customer?.email || "", category: customer?.category || "" });
    setErrs({}); setAdding(false); setNewCat("");
    api.get("/vendor/customer-categories").then(({ data }) => setCats(data)).catch(() => {});
  }, [open, customer]);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setErrs((e) => ({ ...e, [k]: undefined })); };

  const addCategory = async () => {
    const name = newCat.trim().replace(/\s+/g, " ");
    if (name.length < 2) return setErrs((e) => ({ ...e, newCat: "Enter at least 2 characters." }));
    setCatBusy(true);
    try {
      const { data } = await api.post("/vendor/customer-categories", { name });
      setCats(data.categories); set("category", data.name); setAdding(false); setNewCat("");
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      if (err.response?.status === 409) { const hit = cats.find((c) => c.toLowerCase() === name.toLowerCase()); if (hit) { set("category", hit); setAdding(false); setNewCat(""); return; } }
      setErrs((e) => ({ ...e, newCat: msg }));
    } finally { setCatBusy(false); }
  };

  const submit = async () => {
    const all = personErrors({ ...f });
    const e = Object.fromEntries(["name", "mobile", "email"].filter((k) => all[k]).map((k) => [k, all[k]]));
    if (adding) e.newCat = "Add the new category, or cancel it.";
    setErrs(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const body = { name: f.name.trim(), mobile: f.mobile, email: f.email.trim(), category: f.category };
    try {
      const { data } = customer ? await api.put(`/vendor/customers/${customer.id}`, body) : await api.post("/vendor/customers", body);
      toast.success(customer ? "Customer updated." : "Customer added.");
      onSaved?.(data);
      onClose();
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      if (err.response?.status === 409) setErrs({ [/email/i.test(msg) ? "email" : "mobile"]: msg });     // already exists: show it on the field
      else toast.error(msg);
    } finally { setSaving(false); }
  };

  const cls = (k) => `${inputCls} ${errs[k] ? inputErrCls : ""}`;
  const Err = ({ k }) => (errs[k] ? <p role="alert" className="mt-1 text-[11px] text-red-600">{errs[k]}</p> : null);
  return (
    <Modal open={open} onClose={onClose} size="md" title={customer ? "Edit customer" : "Add customer"} subtitle={customer ? "Manage addresses from the Addresses tab." : "Addresses can be added afterwards."} testId="customer-modal"
      footer={<><button onClick={onClose} className={btnOutline}>Cancel</button><button onClick={submit} disabled={saving} data-testid="customer-save" className={btnPrimary}>{saving ? "Saving…" : customer ? "Save changes" : "Add customer"}</button></>}>
      <div className="space-y-3 text-[13px]">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Name *</label><input value={f.name} maxLength={80} onChange={(e) => set("name", e.target.value)} data-testid="customer-name" className={cls("name")} /><Err k="name" /></div>
          <div><label className={labelCls}>Phone *</label><input value={f.mobile} maxLength={16} onChange={(e) => set("mobile", e.target.value)} data-testid="customer-mobile" inputMode="numeric" className={cls("mobile")} /><Err k="mobile" /></div>
        </div>
        <div><label className={labelCls}>Email <span className="font-normal text-slate-400">(optional)</span></label><input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="customer-email" className={cls("email")} /><Err k="email" /></div>
        <div>
          <label className={labelCls}>Category <span className="font-normal text-slate-400">(optional)</span></label>
          <select value={adding ? NEW : f.category} onChange={(e) => { if (e.target.value === NEW) { setAdding(true); } else { setAdding(false); set("category", e.target.value); } }} data-testid="customer-category" className={inputCls}>
            <option value="">No category</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value={NEW}>+ Add new category…</option>
          </select>
          {adding && (
            <div className="mt-2 flex gap-2">
              <input autoFocus value={newCat} maxLength={40} onChange={(e) => { setNewCat(e.target.value); setErrs((x) => ({ ...x, newCat: undefined })); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                placeholder="New category name, e.g. Corporate" data-testid="customer-new-category" className={cls("newCat")} />
              <button type="button" onClick={addCategory} disabled={catBusy} data-testid="customer-add-category" className={btnPrimary}>{catBusy ? "…" : "Add"}</button>
              <button type="button" onClick={() => { setAdding(false); setNewCat(""); setErrs((x) => ({ ...x, newCat: undefined })); }} className={btnOutline}>Cancel</button>
            </div>
          )}
          <Err k="newCat" />
        </div>
      </div>
    </Modal>
  );
}
